import { Haptics, NotificationType } from "@capacitor/haptics"

import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import type { IncomeDocument, IncomeErrors, TripLineItem } from '@/types/income'

import Button from "@/components/common/Button/Button"
import MotionText from "@/components/common/Motion/MotionText"
import MotionDiv from "@/components/common/Motion/MotionDiv"
import HeaderBack from "@/components/dashboard/Header/HeaderBack"
import DecimalInput from "@/components/common/Input/DecimalInput"
import DateInput from "@/components/common/Input/DateInput"

import { useKycStore } from "@/store/useKycStore"

import Input from "@/components/common/Input/Input"
import ProgressBar from "@/components/common/ProgressBar/ProgressBar"

import incomeAPI from "@/services/api/income"
import monthlyStatementsAPI from "@/services/api/monthlyStatements"
import { useUberStore } from "@/store/useUberStore"
import { useUserStore } from "@/store/useUserStore"
import { useDashboardStore } from "@/store/useDashboardStore"
import { requestInAppReview } from "@/services/review"
import { usePageSetup } from "@/hooks/usePageSetup"

export const UberIncomePage = () => {
  const { t } = useTranslation()
  usePageSetup(t('uber.pageTitle.income'))
  const user = useUserStore((state) => state.user)
  const csvStepEnabled = user?.uber_status === 'enabled'

  const progress = useKycStore((state) => state.progress)
  const setProgress = useKycStore((state) => state.setProgress)

  useEffect(() => setProgress(csvStepEnabled ? 3 : 2), [])

  const navigate = useNavigate()

  const balance = useUberStore((state) => state.balanceAnalysis as IncomeDocument)
  const trips = useUberStore((state) => state.tripAnalysis as TripLineItem[])
  const resetAnalysis = useUberStore((state) => state.resetAnalysis)
  const dashboard = useDashboardStore((state) => state.dashboard)

  const lineItems = balance?.line_items || []

  const tripsTotal = trips?.reduce((sum, { amount }) => sum + amount, 0) || 0

  const balanceTrip = trips?.length > 0 ? tripsTotal : lineItems.reduce((sum, item) => {
    return (item.category === 'trip' || item.category === 'airport' || item.category === 'booking')
      ? sum + (item.amount || 0)
      : sum
  }, 0)

  const balanceBonus = lineItems.reduce((sum, item) => {
    return (item.category === 'bonus' || item.category === 'misc')
      ? sum + (item.amount || 0)
      : sum
  }, 0)

  const balanceTips = lineItems.reduce((sum, item) => (
    item.category === 'tips' ? sum + (item.amount || 0) : sum
  ), 0)

  const balanceTripOperation = lineItems.find(
    item => item.category === 'trip'
  )?.description || ''

  const balanceBonusOperation = lineItems.find(
    item => item.category === 'bonus'
  )?.description || ''

  const balanceTipsOperation = lineItems.find(
    item => item.category === 'tips'
  )?.description || ''

  const [trip, setTrip] = useState(balanceTrip)
  const [bonus, setBonus] = useState(balanceBonus)
  const [tips, setTips] = useState(balanceTips)

  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<IncomeErrors>({})

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    setIsLoading(true)

    /* Persist the analyzed monthly statement (importantly the distance_km)
       alongside the incomes */
    const statementPromise = () => {
      if (!balance?.reference?.date || lineItems.length === 0) return false

      return monthlyStatementsAPI.create({
        source: 'uber',
        date: balance.reference.date,
        distance_km: lineItems.find(item => item.category === 'distance_km')?.amount,
        line_items: lineItems
      })
    }

    if (trips?.length > 0) {
      const referenceDate = balance?.reference?.date || trips[0].date
      const date = new Date(referenceDate);
      const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0)

      const formattedDate = new Intl.DateTimeFormat(
        "lt-LT",
        { year: 'numeric', month: '2-digit', day: '2-digit' }
      ).format(lastDayOfMonth)

      const promises: (Promise<unknown> | false)[] = [
        bonus > 0 && incomeAPI.create({
          income_category_code: "uber_bonus",
          amount_cents: Math.round(bonus * 100),
          date: formattedDate,
          reference: [formattedDate, t('uber.income.bonusReference')].join(', '),
          operation: balanceBonusOperation || t('common.bonusOperation')
        }),
        tips > 0 && incomeAPI.create({
          income_category_code: "uber_tips",
          amount_cents: Math.round(tips * 100),
          date: formattedDate,
          reference: [formattedDate, t('uber.income.tipsReference')].join(', '),
          operation: balanceTipsOperation || t('common.tipsOperation')
        }),
      ]

      trips.forEach(trip => {
        promises.push(
          incomeAPI.create({
            income_category_code: "uber_trip",
            amount_cents: Math.round(trip.amount * 100),
            date: trip.date,
            reference: [trip.date, trip.reference].join(', '),
            operation: t('common.tripOperation')
          })
        )
      })

      promises.push(statementPromise())

      /* Duplicate references (re-uploading a month) are skipped by the API —
         the driver can delete the month and re-upload for a clean redo */
      await Promise.allSettled(promises.filter(Boolean) as Promise<unknown>[])

      resetAnalysis()
      setIsLoading(false)
      navigate("/income")
      Haptics.notification({ type: NotificationType.Success })
      requestInAppReview(dashboard)
    } else if (balance?.reference?.date) {
      const date = new Date(balance.reference.date);
      const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0)

      const formattedDate = new Intl.DateTimeFormat(
        "lt-LT",
        { year: 'numeric', month: '2-digit', day: '2-digit' }
      ).format(lastDayOfMonth)

      const promises = [
        trip > 0 && incomeAPI.create({
          income_category_code: "uber_trip",
          amount_cents: Math.round(trip * 100),
          date: formattedDate,
          reference: [formattedDate, t('uber.income.tripReference')].join(', '),
          operation: balanceTripOperation || t('common.tripOperation')
        }),
        bonus > 0 && incomeAPI.create({
          income_category_code: "uber_bonus",
          amount_cents: Math.round(bonus * 100),
          date: formattedDate,
          reference: [formattedDate, t('uber.income.bonusReference')].join(', '),
          operation: balanceBonusOperation || t('common.bonusOperation')
        }),
        tips > 0 && incomeAPI.create({
          income_category_code: "uber_tips",
          amount_cents: Math.round(tips * 100),
          date: formattedDate,
          reference: [formattedDate, t('uber.income.tipsReference')].join(', '),
          operation: balanceTipsOperation || t('common.tipsOperation')
        }),
        statementPromise(),
      ]

      await Promise.allSettled(promises.filter(Boolean) as Promise<unknown>[])

      resetAnalysis()
      setIsLoading(false)
      navigate("/income")
      Haptics.notification({ type: NotificationType.Success })
      requestInAppReview(dashboard)
    } else {
      const form = new FormData(e.currentTarget)
      const date = form.get('date') as string
      const reference = form.get('reference') as string

      const promises = [
        trip > 0 && incomeAPI.create({
          income_category_code: "uber_trip",
          amount_cents: Math.round(trip * 100),
          date: date,
          reference: `${reference} ${t('uber.income.tripReference')}`,
          operation: t('common.tripOperation')
        }),
        bonus > 0 && incomeAPI.create({
          income_category_code: "uber_bonus",
          amount_cents: Math.round(bonus * 100),
          date: date,
          reference: `${reference} ${t('uber.income.bonusReference')}`,
          operation: t('common.bonusOperation')
        }),
        tips > 0 && incomeAPI.create({
          income_category_code: "uber_tips",
          amount_cents: Math.round(tips * 100),
          date: date,
          reference: `${reference} ${t('uber.income.tipsReference')}`,
          operation: t('common.tipsOperation')
        }),
      ]

      try {
        await Promise.all(promises)
        navigate("/income")
        Haptics.notification({ type: NotificationType.Success })
        requestInAppReview(dashboard)
      } catch {
        setErrors({ reference: [t('common.referenceExists')] })
        setIsLoading(false)
      }
    }
  }

  const hasDocumentData = typeof balance?.reference?.date === 'string' || trips?.length > 0

  return (
    <form onSubmit={handleSubmit} className="h-full bg-white flex flex-col">
      <HeaderBack>
        <div className="flex flex-row items-center w-[8.5rem] ml-[3.84rem]">
          <ProgressBar
            currentStep={progress}
            totalSteps={csvStepEnabled ? 3 : 2}
          />
        </div>
      </HeaderBack>
      <div className="h-full flex flex-col gap-4 px-5 pb-8">
        <MotionText
          as="h2"
          className="font-semibold text-3xl text-zinc-900"
          delay={0.1}
        >
          {t('uber.income.title')}
        </MotionText>
        <MotionDiv>
          <DecimalInput
            className="w-[100%]"
            inputMode='decimal'
            name='income'
            label={t('common.tripIncome')}
            max={9999}
            postfix="\€"
            onChange={value => setTrip(parseFloat(value))}
            defaultValue={balanceTrip}
            required
            disabled={balanceTrip > 0}
          />
        </MotionDiv>
        <MotionDiv className="flex flex-row gap-4">
          <DecimalInput
            className="w-[100%]"
            inputMode='decimal'
            name='bonus'
            label={t('common.bonus')}
            max={9999}
            postfix="\€"
            onChange={value => setBonus(parseFloat(value))}
            defaultValue={balanceBonus}
            optional={true}
            optionalText={t('common.ifAny')}
          />
          <DecimalInput
            className="w-[100%]"
            inputMode='decimal'
            name='tips'
            label={t('common.tips')}
            max={9999}
            postfix="\€"
            onChange={value => setTips(parseFloat(value))}
            defaultValue={balanceTips}
            optional={true}
            optionalText={t('common.ifAny')}
          />
        </MotionDiv>
        {
          !hasDocumentData && <>
            <MotionDiv>
              <DateInput
                inputMode="numeric"
                className="w-[100%]"
                name='date'
                label={t('common.incomeDate')}
                placeholder={new Date().toISOString().split('T')[0]}
                required
              />
            </MotionDiv>
            <MotionDiv className="pb-[7rem]">
              <Input
                name='reference'
                label={t('common.documentReference')}
                required
                error={errors?.reference?.[0]}
              />
            </MotionDiv>
          </>
        }
        <div className="absolute bottom-0 -ml-5 px-5 pb-8 pt-8 w-full bg-linear-to-t from-white via-white to-transparent from-0% via-40% to-100%">
          <Button type="submit" isLoading={isLoading}>
            <span>{t('common.save')}</span>
          </Button>
        </div>
      </div>
    </form>
  )
}

export default UberIncomePage
